/**
 * THE PLAYER'S OWN SCREEN: an email, a screen name and a four-digit code.
 * That is the whole profile a person needs to play and to have their winnings
 * banked. No points programme, no pitch, no gate — those live elsewhere.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { saveErrorKey, takenBy } from './goalSaveError';
import { emailSuggestion } from './emailTypo';

/** WHO INVITED THEM. A friend's personal link carries ?ref=…; it is kept on
 *  this phone so the sign-up finishes even if the player reloads the page.
 *  It is a record only — nothing is paid for it. */
export const inviteRef = () => {
    try {
        const fromUrl = new URLSearchParams(window.location.search).get('ref') || '';
        if (fromUrl) localStorage.setItem('goalInviteRef', fromUrl);
        return fromUrl || localStorage.getItem('goalInviteRef') || '';
    } catch (_e) { return ''; }
};

export const GoalPlayerAccount = ({ cardId, t, c, ty, lang, field, primaryBtn,
                                    deviceId, onDone, startBack = false, atHome = null }) => {
    const [back, setBack] = useState(!!startBack);
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [code, setCode] = useState('');
    const [consent, setConsent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const p = (k) => t(`goalGame.player.${k}`);
    const suggestion = emailSuggestion(email);

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        if (!/^\d{4}$/.test(code)) { setError(p('codeFour')); return; }
        if (!back && !name.trim()) { setError(p('nameNeeded')); return; }
        setBusy(true);
        try {
            const res = await fetch(`/api/patron/public/${cardId}/account`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: email.trim(), pin: code, consent: back ? true : consent,
                    screen_name: name.trim(), lang, device_id: deviceId || '',
                    at_home: atHome, ref: inviteRef(),
                    // WHICH SCREEN THIS IS. Signing in may only ever sign an
                    // existing player in; it never creates an account.
                    mode: back ? 'signin' : 'new',
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const who = takenBy(data.detail);
                setError(who
                    ? t('goalGame.saveErrors.phoneTakenNamed', { player: who })
                    : t(`goalGame.saveErrors.${saveErrorKey(res.status, data.detail)}`));
                return;
            }
            onDone?.(data, { email: email.trim(), pin: code, consent: true });
        } catch (_e) {
            setError(t('goalGame.saveErrors.offline'));
        } finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} className="space-y-3 text-left"
              data-testid="goal-player-account">
            <p className="text-xl font-black" style={ty.headline}
               data-testid="goal-player-title">
                {back ? p('backTitle') : p('newTitle')}
            </p>
            <p className="text-sm opacity-75" data-testid="goal-player-body">
                {back ? p('backBody') : p('newBody')}
            </p>
            <input type="email" required value={email} autoComplete="email"
                   onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
                   placeholder={p('emailLabel')} className={field}
                   data-testid="goal-player-email" />
            {/* A common domain slip, offered in one tap. Never a block: the
                player keeps exactly what they typed unless they tap it. */}
            {suggestion && (
                <button type="button" onClick={() => setEmail(suggestion)}
                        data-testid="goal-player-email-suggest"
                        className="block text-left text-xs underline opacity-90">
                    {t('goalGame.player.didYouMean', { email: suggestion })}
                </button>
            )}
            {!back && (
                <input type="text" required value={name} maxLength={24}
                       onChange={(e) => setName(e.target.value)}
                       placeholder={p('namePlaceholder')} className={field}
                       data-testid="goal-player-name" />
            )}
            <label className="block">
                <span className="block text-xs opacity-60 mb-1">
                    {back ? p('codePlaceholder') : p('codeLabel')}
                </span>
                <input type="tel" inputMode="numeric" autoComplete="off" maxLength={4}
                       value={code}
                       onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                       placeholder={p('codePlaceholder')}
                       className={`${field} tracking-[0.4em] text-center`}
                       data-testid="goal-player-code" />
            </label>
            {!back && (
                <>
                    <label className="flex items-start gap-2 text-xs opacity-80">
                        <input type="checkbox" checked={consent} required
                               onChange={(e) => setConsent(e.target.checked)}
                               className="mt-0.5" data-testid="goal-player-consent" />
                        <span>{p('consent')}</span>
                    </label>
                    <p className="text-[11px] opacity-60 -mt-1" data-testid="goal-player-boards">
                        {p('boards')}
                    </p>
                    <p className="text-[11px] opacity-60 -mt-1" data-testid="goal-player-law">
                        {p('law')}
                    </p>
                </>
            )}
            <button type="submit" disabled={busy} style={primaryBtn}
                    data-testid="goal-player-submit"
                    className="w-full min-h-[52px] rounded-xl py-3 text-base font-bold shadow-lg disabled:opacity-60">
                {busy ? <Loader2 className="mx-auto animate-spin" size={20} />
                    : (back ? p('signIn') : p('start'))}
            </button>
            <button type="button" onClick={() => { setBack(!back); setError(''); }}
                    data-testid="goal-player-switch"
                    className="w-full text-xs underline opacity-80 py-1">
                {back ? p('newHere') : p('haveCode')}
            </button>
            {error && (
                <p className="text-sm font-semibold" style={{ color: c?.bad || '#fca5a5' }}
                   data-testid="goal-player-error">{error}</p>
            )}
        </form>
    );
};
